import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Target, LockKey, CircleNotch } from "@phosphor-icons/react";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand to-brand-900 shadow-lg mb-4">
            <Target className="w-8 h-8 text-white" weight="duotone" />
          </span>
          <h1 className="font-heading font-black text-xl text-slate-900">Guwahati Morning Teer</h1>
          <p className="text-sm font-bold text-brand tracking-widest">ADMIN PANEL</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl p-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full h-14 px-4 rounded-xl border border-slate-200 text-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
              placeholder="admin@…"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full h-14 px-4 rounded-xl border border-slate-200 text-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          {error && <p className="text-sm text-red-600 font-medium bg-red-50 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full h-14 rounded-xl bg-brand text-white font-bold text-lg flex items-center justify-center gap-2 hover:bg-brand-hover transition-colors disabled:opacity-60"
          >
            {loading ? <CircleNotch className="w-5 h-5 animate-spin" /> : <LockKey className="w-5 h-5" weight="bold" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p className="text-center text-xs text-slate-400 mt-6">Authorized access only.</p>
      </div>
    </div>
  );
}
